import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import RecruitmentXLogo from './rx_logo_white.svg';

const SubscriptionPlansPage = () => {
    const [plans, setPlans] = useState({ monthly: [], oneoff: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState('');
    const [checkout, setCheckout] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    
    // New states for billing type tabs and duration selection
    const [activeBillingTab, setActiveBillingTab] = useState('monthly');
    const [selectedDuration, setSelectedDuration] = useState({
        basic: '1year',
        standard: '1year', 
        premium: '1year'
    });
    const [showTrialOptions, setShowTrialOptions] = useState(false);
    const [selectedTrialType, setSelectedTrialType] = useState(null);

    // Get backend URL from environment variable or use relative path
    const backend_url = process.env.REACT_APP_BACKEND_URL || '';

    useEffect(() => {
        // Fetch Stripe configuration from backend
        const initializeStripe = async () => {
            try {
                const response = await fetch(`${backend_url}/v1/stripe-config`);
                const data = await response.json();
                if (data.success && data.config.publishable_key) {
                    setStripePromise(loadStripe(data.config.publishable_key));
                } else {
                    console.error('Failed to load Stripe configuration');
                }
            } catch (error) {
                console.error('Error loading Stripe configuration:', error);
            }
        };
        
        initializeStripe();
        fetchSubscriptionPlans();
    }, []);

    // Initialize Payment Element when DOM is ready
    useEffect(() => {
        const initializePaymentElement = async () => {
            if (showPaymentForm && paymentData && !checkout) {
                try {
                    const stripe = await stripePromise;
                    const elements = stripe.elements({
                        clientSecret: paymentData.clientSecret,
                    });
                    
                    const paymentElement = elements.create('payment');
                    paymentElement.mount('#payment-element');
                    
                    // Store for payment confirmation
                    setCheckout({ stripe, elements });
                } catch (elementError) {
                    console.error('Error initializing Payment Element:', elementError);
                    setPaymentStatus('Failed to initialize payment form. Please try again.');
                }
            }
        };

        initializePaymentElement();
    }, [showPaymentForm, paymentData, checkout, stripePromise]);


    const fetchSubscriptionPlans = async () => {
        try {
            const response = await fetch(`${backend_url}/v1/subscription-plans`);
            const data = await response.json();
            
            console.log('Received plans:', data);
            
            if (!data.success) {
                console.error('Failed to fetch plans:', data.error);
                return;
            }
            
            // Backend returns { success: true, monthly: [...], oneoff: [...] }
            const monthlyPlans = data.monthly || [];
            const oneoffPlans = data.oneoff || [];
            
            // Order monthly plans: Test, Basic, Standard, Premium
            const planOrder = ['test_monthly', 'basic_monthly', 'standard_monthly', 'premium_monthly'];
            const orderedMonthlyPlans = [];
            
            planOrder.forEach(planId => {
                const plan = monthlyPlans.find(p => p.id === planId);
                if (plan) {
                    orderedMonthlyPlans.push(plan);
                }
            });
            
            console.log('Monthly plans:', orderedMonthlyPlans);
            console.log('Oneoff plans:', oneoffPlans);
            
            setPlans({ monthly: orderedMonthlyPlans, oneoff: oneoffPlans });
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const generatePlanFeatures = (plan) => {
        const features = [
            `${plan.yearly_cv_limit} CVs per year`
        ];
        
        return features;
    };

    const calculateDiscount = (oneoffPlan) => {
        const basePlanName = oneoffPlan.id.replace(/_\d+year$/, '') + '_monthly';
        const monthlyPlan = plans.monthly?.find(p => p.id === basePlanName);
        
        if (!monthlyPlan) return 0;
        
        const monthlyCost = monthlyPlan.price * oneoffPlan.duration;
        const savings = Math.round((1 - oneoffPlan.price / monthlyCost) * 100);
        return savings;
    };

    const getOneoffPlanForTier = (tier) => {
        const tierPlans = plans.oneoff?.filter(p => p.id.includes(tier));
        const duration = selectedDuration[tier] || '1year';
        return tierPlans?.find(p => p.id === `${tier}_${duration}`);
    };

    const handleDurationChange = (tier, duration) => {
        setSelectedDuration(prev => ({
            ...prev,
            [tier]: duration
        }));
    };

    const handlePlanSelect = async (plan, trialSignupType = null) => {
        // For free trial, show options if not already selected
        if (plan.id === 'freetrail_7day' && !trialSignupType) {
            setSelectedPlan(plan);
            setShowTrialOptions(true);
            return;
        }

        setSelectedPlan(plan);
        setIsProcessing(true);

        try {
            // Get user information from localStorage
            const pendingCorporation = localStorage.getItem('pendingCorporation');
            let customerId = '';
            let userEmail = 'user@example.com';
            let userName = 'User Name';
            
            if (pendingCorporation) {
                try {
                    const corpInfo = JSON.parse(pendingCorporation);
                    customerId = corpInfo.corporateId || '';
                    
                    // Primary User information
                    if (corpInfo.primaryUser) {
                        userEmail = corpInfo.primaryUser.email || userEmail;
                        userName = `${corpInfo.primaryUser.firstName} ${corpInfo.primaryUser.lastName}`.trim() || userName;
                    }
                } catch (error) {
                    console.error('Error parsing pending corporation data:', error);
                }
            }

            if (!customerId) {
                console.error('No corporate ID found in localStorage');
                alert('Session expired. Please sign up again.');
                window.location.href = '/signup';
                return;
            }

            console.log('Creating payment intent with corporate ID:', customerId);
            console.log('Using primary user for billing:', {
                email: userEmail,
                name: userName,
                corporateId: customerId
            });

            // Prepare request body
            const requestBody = {
                plan_id: plan.id,
                customer_id: customerId,
                email: userEmail, 
                name: userName
            };

            // Add trial_signup_type for free trial
            if (plan.id === 'freetrail_7day' && trialSignupType) {
                requestBody.trial_signup_type = trialSignupType;
            }

            // Create Stripe payment intent
            const response = await fetch(`${backend_url}/v1/create-payment-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();
            
            if (data.success) {
                // For direct_access trial, redirect to success page
                if (data.step === 'trial_activated' && data.trial_type === 'direct_access') {
                    localStorage.removeItem('pendingCorporation');
                    window.location.href = '/payment-success';
                    return;
                }

                // For payment method collection, show payment form
                if (data.client_secret) {
                    setPaymentData({
                        clientSecret: data.client_secret,
                        paymentIntentId: data.payment_intent_id,
                        setupIntentId: data.setup_intent_id,
                        customerId: data.customer_id,
                        amount: data.amount,
                        currency: data.currency,
                        step: data.step 
                    });
                    setShowPaymentForm(true);
                } else {
                    console.error('Unexpected response format:', data);
                    alert('Failed to initialize payment. Please try again.');
                }
            } else {
                console.error('Failed to create payment intent:', data.error);
                alert('Failed to initialize payment. Please try again.');
            }
        } catch (error) {
            console.error('Error creating payment intent:', error);
            alert('Error creating payment intent. Please try again.');
        } finally {
            setIsProcessing(false);
            setShowTrialOptions(false);
        }
    };


    const handleBackToPlans = () => {
        setShowPaymentForm(false);
        setSelectedPlan(null);
        setPaymentData(null);
        setPaymentStatus('');
        setCheckout(null);
    };

    const handlePaymentSubmit = async (event) => {
        event.preventDefault();
        
        if (!checkout || !checkout.stripe || !checkout.elements) {
            console.error('Stripe not initialized');
            setPaymentStatus('Payment system not ready. Please try again.');
            return;
        }

        setPaymentStatus('Processing payment...');

        // Check if this is a SetupIntent (monthly subscription) or PaymentIntent (oneoff)
        const isSetupIntent = paymentData?.step === 'collect_payment_method';

        if (isSetupIntent) {
            // Monthly subscription: use confirmSetup
            console.log('Confirming SetupIntent for monthly subscription...');
            const { error } = await checkout.stripe.confirmSetup({
                elements: checkout.elements,
                confirmParams: {
                    return_url: `${window.location.origin}/payment-success`,
                },
            });

            if (error) {
                console.error('Setup failed:', error);
                setPaymentStatus(`Setup failed: ${error.message}`);
            } else {
                // Payment successful - clean up localStorage
                localStorage.removeItem('pendingCorporation');
                console.log('Payment successful, cleaned up localStorage');
            }
        } else {
            // Oneoff payment: use confirmPayment
            console.log('Confirming PaymentIntent for oneoff payment...');
            const { error } = await checkout.stripe.confirmPayment({
                elements: checkout.elements,
                confirmParams: {
                    return_url: `${window.location.origin}/payment-success`,
                },
            });

            if (error) {
                console.error('Payment failed:', error);
                setPaymentStatus(`Payment failed: ${error.message}`);
            } else {
                // Payment successful - clean up localStorage
                localStorage.removeItem('pendingCorporation');
                console.log('Payment successful, cleaned up localStorage');
            }
        }
    };

    const styles = {
        container: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #141414 0%, #1f1520 50%, #141414 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            position: 'relative',
            overflow: 'hidden'
        },
        backgroundPattern: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.03) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
        },
        header: {
            textAlign: 'center',
            marginBottom: '60px',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        logo: {
            width: '300px',
            height: 'auto',
            marginBottom: '40px',
            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
        },
        title: {
            fontSize: '32px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '16px',
            letterSpacing: '-0.02em'
        },
        subtitle: {
            fontSize: '14px',
            color: '#a0a0a0',
            fontWeight: '400',
            maxWidth: '700px',
            lineHeight: '1.6',
            margin: '0 auto'
        },
        plansContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px',
            maxWidth: '1400px',
            width: '100%',
            zIndex: 1
        },
        planCard: {
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '40px 32px',
            textAlign: 'center',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        },
        planCardHover: {
            transform: 'translateY(-8px)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
        },
        planName: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '12px',
            letterSpacing: '-0.01em'
        },
        planPrice: {
            fontSize: '48px',
            fontWeight: '800',
            color: '#ffffff',
            marginBottom: '8px',
            letterSpacing: '-0.02em'
        },
        planCurrency: {
            fontSize: '20px',
            color: '#a0a0a0',
            fontWeight: '500'
        },
        planInterval: {
            fontSize: '16px',
            color: '#808080',
            marginBottom: '32px'
        },
        planFeatures: {
            listStyle: 'none',
            padding: 0,
            margin: '0 0 auto 0',
            paddingBottom: '40px',
            flex: 1
        },
        planFeature: {
            fontSize: '16px',
            color: '#e0e0e0',
            marginBottom: '12px',
            paddingLeft: '24px',
            position: 'relative'
        },
        planFeatureIcon: {
            position: 'absolute',
            left: 0,
            top: '2px',
            width: '16px',
            height: '16px',
            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#ffffff',
            fontWeight: 'bold'
        },
        planButton: {
            width: '100%',
            padding: '16px 32px',
            fontSize: '18px',
            fontWeight: '600',
            borderRadius: '12px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden'
        },
        planButtonPrimary: {
            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
            color: '#ffffff',
            boxShadow: '0 8px 16px rgba(255, 0, 102, 0.3)'
        },
        planButtonPrimaryHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 24px rgba(255, 0, 102, 0.4)'
        },
        planButtonSecondary: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.2)'
        },
        planButtonSecondaryHover: {
            background: 'rgba(255, 255, 255, 0.15)',
            borderColor: 'rgba(255, 255, 255, 0.3)'
        },
        loadingSpinner: {
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            borderTopColor: '#ffffff',
            animation: 'spin 1s ease-in-out infinite',
            marginRight: '8px'
        },
        paymentForm: {
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            zIndex: 1
        },
        paymentFormTitle: {
            fontSize: '32px',
            fontWeight: '700',
            color: '#ffffff',
            textAlign: 'center',
            marginBottom: '32px'
        },
        paymentElement: {
            marginBottom: '24px',
            padding: '16px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
        },
        paymentButton: {
            width: '100%',
            padding: '16px 32px',
            fontSize: '18px',
            fontWeight: '600',
            borderRadius: '12px',
            border: 'none',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
            color: '#ffffff',
            boxShadow: '0 8px 16px rgba(255, 0, 102, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        },
        backButton: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            marginBottom: '16px'
        },
        statusMessage: {
            textAlign: 'center',
            fontSize: '16px',
            marginTop: '16px',
            padding: '12px',
            borderRadius: '8px',
            background: 'rgba(255, 0, 102, 0.1)',
            color: '#FF66CC',
            border: '1px solid rgba(255, 0, 102, 0.2)'
        },
        billingTabsContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '40px',
            zIndex: 1
        },
        billingTab: {
            padding: '12px 32px',
            fontSize: '16px',
            fontWeight: '600',
            borderRadius: '24px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '0 8px'
        },
        billingTabActive: {
            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
            color: '#ffffff',
            boxShadow: '0 8px 16px rgba(255, 0, 102, 0.3)'
        },
        billingTabInactive: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#a0a0a0'
        },
        durationSelector: {
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '20px'
        },
        durationOption: {
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#e0e0e0'
        },
        durationOptionActive: {
            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
            color: '#ffffff',
            borderColor: 'transparent'
        },
        priceContainer: {
            marginBottom: '16px'
        },
        discountBadge: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#ffffff',
            background: 'rgba(0, 255, 0, 0.2)',
            border: '1px solid rgba(0, 255, 0, 0.3)',
            borderRadius: '8px',
            padding: '2px 6px',
            display: 'inline-block'
        }
    };

    if (isLoading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingSpinner}></div>
                <div style={{ color: '#a0a0a0', marginTop: '16px' }}>Loading plans...</div>
            </div>
        );
    }

    if (showPaymentForm && selectedPlan && paymentData) {
        return (
            <div style={styles.container}>
                <div style={styles.backgroundPattern}></div>
                <div style={styles.paymentForm}>
                    <h2 style={styles.paymentFormTitle}>
                        Complete Payment for {selectedPlan.name}
                    </h2>
                    <button 
                        style={{...styles.paymentButton, ...styles.backButton}}
                        onClick={handleBackToPlans}
                    >
                        ← Back to Plans
                    </button>
                    <form onSubmit={handlePaymentSubmit}>
                        <div id="payment-element" style={styles.paymentElement}>
                            {/* Payment Element will be mounted here */}
                        </div>
                        <button 
                            type="submit" 
                            style={styles.paymentButton}
                            disabled={paymentStatus === 'Processing payment...'}
                        >
                            {paymentStatus === 'Processing payment...' ? (
                                <>
                                    <span style={styles.loadingSpinner}></span>
                                    Processing...
                                </>
                            ) : (
                                `Pay A$${(paymentData.amount / 100).toFixed(2)}`
                            )}
                        </button>
                    </form>
                    {paymentStatus && (
                        <div style={styles.statusMessage}>
                            {paymentStatus}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.backgroundPattern}></div>
            
            <div style={styles.header}>
                <img src={RecruitmentXLogo} alt="Recruitment X" style={styles.logo} />
                <h1 style={styles.title}>Choose Your Plan</h1>
                <p style={styles.subtitle}>
                    Select the perfect plan for your recruitment needs. All plans include our core CV ranking features.
                </p>
            </div>

            {/* Billing Type Tabs */}
            <div style={styles.billingTabsContainer}>
                <button
                    style={{
                        ...styles.billingTab,
                        ...(activeBillingTab === 'monthly' ? styles.billingTabActive : styles.billingTabInactive)
                    }}
                    onClick={() => setActiveBillingTab('monthly')}
                >
                    Recurring Plans
                </button>
                <button
                    style={{
                        ...styles.billingTab,
                        ...(activeBillingTab === 'oneoff' ? styles.billingTabActive : styles.billingTabInactive)
                    }}
                    onClick={() => setActiveBillingTab('oneoff')}
                >
                    One-off Plans
                </button>
            </div>

            <div style={styles.plansContainer}>
                {activeBillingTab === 'monthly' ? (
                    // Recurring Plans
                    <>
                        {/* Free Trial - Show first in monthly tab */}
                        {(() => {
                            const freeTrialPlan = plans.oneoff?.find(p => p.id === 'freetrail_7day');
                            if (freeTrialPlan) {
                                return (
                                    <div
                                        key={freeTrialPlan.id}
                                        style={{
                                            ...styles.planCard,
                                            ...(selectedPlan?.id === freeTrialPlan.id ? styles.planCardHover : {})
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-8px)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        <div style={{
                                            position: 'absolute',
                                            top: '16px',
                                            right: '16px',
                                            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            letterSpacing: '1px',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            boxShadow: '0 4px 12px rgba(255, 0, 102, 0.4)'
                                        }}>
                                            FREE TRIAL
                                        </div>
                                        
                                        <h3 style={styles.planName}>{freeTrialPlan.name}</h3>
                                        
                                        <div style={styles.priceContainer}>
                                            <div style={{fontSize: '16px', color: '#808080', marginTop: '20px'}}>
                                                for {freeTrialPlan.duration} days
                                            </div>
                                        </div>
                                        
                                        <ul style={styles.planFeatures}>
                                            <li style={styles.planFeature}>
                                                <span style={styles.planFeatureIcon}>✓</span>
                                                {freeTrialPlan.yearly_cv_limit} CVs to test
                                            </li>
                                        </ul>
                                        
                                        {showTrialOptions && selectedPlan?.id === freeTrialPlan.id ? (
                                            <div>
                                                <p style={{fontSize: '13px', color: '#a0a0a0', marginBottom: '16px', textAlign: 'center'}}>
                                                    Choose how to start:
                                                </p>
                                                <button
                                                    style={{
                                                        ...styles.planButton,
                                                        ...styles.planButtonPrimary,
                                                        marginBottom: '10px',
                                                        fontSize: '15px',
                                                        padding: '14px 24px'
                                                    }}
                                                    onClick={() => handlePlanSelect(freeTrialPlan, 'with_payment_method')}
                                                    disabled={isProcessing}
                                                >
                                                    {isProcessing ? (
                                                        <>
                                                            <span style={styles.loadingSpinner}></span>
                                                            Processing...
                                                        </>
                                                    ) : (
                                                        'With Payment Method'
                                                    )}
                                                </button>
                                                <button
                                                    style={{
                                                        ...styles.planButton,
                                                        ...styles.planButtonSecondary,
                                                        fontSize: '15px',
                                                        padding: '14px 24px'
                                                    }}
                                                    onClick={() => handlePlanSelect(freeTrialPlan, 'direct_access')}
                                                    disabled={isProcessing}
                                                >
                                                    {isProcessing ? (
                                                        <>
                                                            <span style={styles.loadingSpinner}></span>
                                                            Processing...
                                                        </>
                                                    ) : (
                                                        'Direct Access'
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                style={{
                                                    ...styles.planButton,
                                                    ...styles.planButtonPrimary
                                                }}
                                                onClick={() => handlePlanSelect(freeTrialPlan)}
                                                disabled={isProcessing}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 0, 102, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 0, 102, 0.3)';
                                                }}
                                            >
                                                Start Free Trial
                                            </button>
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        
                        {/* Monthly Plans */}
                        {plans.monthly.map((plan) => (
                        <div
                            key={plan.id}
                        style={{
                            ...styles.planCard,
                            ...(selectedPlan?.id === plan.id ? styles.planCardHover : {})
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <h3 style={styles.planName}>{plan.name}</h3>
                        <div style={styles.planPrice}>
                            <span style={styles.planCurrency}>A$</span>
                            {(plan.price / 100).toFixed(0)}
                        </div>
                        <div style={styles.planInterval}>per {plan.interval}</div>
                        
                        <ul style={styles.planFeatures}>
                            {generatePlanFeatures(plan).map((feature, index) => (
                                <li key={index} style={styles.planFeature}>
                                    <span style={styles.planFeatureIcon}>✓</span>
                                    {feature}
                                </li>
                            ))}
                        </ul>

                        <button
                            style={{
                                ...styles.planButton,
                                ...styles.planButtonPrimary
                            }}
                            onClick={() => handlePlanSelect(plan)}
                            disabled={isProcessing}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 0, 102, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 0, 102, 0.3)';
                            }}
                        >
                                {isProcessing && selectedPlan?.id === plan.id ? (
                                <>
                                    <span style={styles.loadingSpinner}></span>
                                    Processing...
                                </>
                                ) : (
                                'Subscribe Now'
                                )}
                            </button>
                        </div>
                        ))}
                    </>
                ) : (
                    // One-off Plans
                    (() => {
                        const tiers = ['basic', 'standard', 'premium'];
                        const allPlansToDisplay = [];
                        
                        // Add tier-based plans
                        tiers.forEach((tier) => {
                            const plan = getOneoffPlanForTier(tier);
                            if (plan) {
                                allPlansToDisplay.push({
                                    plan: plan,
                                    isTrial: false
                                });
                            }
                        });
                        
                        return allPlansToDisplay.map(({plan, isTrial}) => {
                            const discount = isTrial ? 0 : calculateDiscount(plan);
                            
                            return (
                                <div
                                    key={plan.id}
                                    style={{
                                        ...styles.planCard,
                                        ...(selectedPlan?.id === plan.id ? styles.planCardHover : {})
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-8px)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    {/* FREE TRIAL badge for trial plan */}
                                    {isTrial && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '16px',
                                            right: '16px',
                                            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            letterSpacing: '1px',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            boxShadow: '0 4px 12px rgba(255, 0, 102, 0.4)'
                                        }}>
                                            FREE TRIAL
                                        </div>
                                    )}
                                    
                                    <h3 style={styles.planName}>{plan.name}</h3>
                                    
                                    {/* Duration Selector - only for non-trial plans */}
                                    {!isTrial && (
                                        <div style={styles.durationSelector}>
                                            {['1year', '2year', '3year'].map((duration) => {
                                                const tier = plan.id.split('_')[0];
                                                return (
                                                    <button
                                                        key={duration}
                                                        style={{
                                                            ...styles.durationOption,
                                                            ...(selectedDuration[tier] === duration ? styles.durationOptionActive : {})
                                                        }}
                                                        onClick={() => handleDurationChange(tier, duration)}
                                                    >
                                                        {duration.replace('year', 'Y')}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    
                                    {/* Price Display */}
                                    <div style={styles.priceContainer}>
                                        {isTrial ? (
                                            <>
                                                <div style={{...styles.planPrice, fontSize: '56px'}}>
                                                    FREE
                                                </div>
                                                <div style={{fontSize: '14px', color: '#808080'}}>
                                                    for {plan.duration} days
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={styles.planPrice}>
                                                    <span style={styles.planCurrency}>A$</span>
                                                    {(plan.price / 100).toFixed(0)}
                                                </div>
                                                <div style={{fontSize: '14px', color: '#808080'}}>
                                                    for {Math.round(plan.duration / 12)} year{Math.round(plan.duration / 12) > 1 ? 's' : ''}
                                                </div>
                                                {discount > 0 && (
                                                    <div style={{marginTop: '8px'}}>
                                                        <span style={styles.discountBadge}>Save {discount}%</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    
                                    <ul style={styles.planFeatures}>
                                        <li style={styles.planFeature}>
                                            <span style={styles.planFeatureIcon}>✓</span>
                                            {plan.yearly_cv_limit} CV{plan.yearly_cv_limit > 1 ? 's' : ''} {isTrial ? 'to test' : 'total'}
                                        </li>
                                    </ul>
                                    
                                    <button
                                        style={{
                                            ...styles.planButton,
                                            ...styles.planButtonPrimary
                                        }}
                                        onClick={() => handlePlanSelect(plan)}
                                        disabled={isProcessing}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 0, 102, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 0, 102, 0.3)';
                                        }}
                                    >
                                        {isProcessing && selectedPlan?.id === plan.id ? (
                                            <>
                                                <span style={styles.loadingSpinner}></span>
                                                Processing...
                                            </>
                                        ) : (
                                            isTrial ? 'Start Free Trial' : 'Subscribe Now'
                                        )}
                                    </button>
                                </div>
                            );
                        });
                    })()
                )}
            </div>
        </div>
    );
};

export default SubscriptionPlansPage;